import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Tooltip,
  Chip,
  Stack,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';
import apiService from '../../services/apiService';
import PageContainer from '../../components/PageContainer';
import { LoadingState } from '../../components/LoadingState';

// Define Release Note interface
interface ReleaseNote {
  id: number;
  title: string;
  description: string;
  version: string | null;
  release_date: string;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
}

// Form state for create/edit dialog
interface ReleaseNoteForm {
  title: string;
  description: string;
  version: string;
  release_date: Date | null;
  is_visible: boolean;
}

// Custom date picker component to avoid type errors
const CustomDatePicker: React.FC<{
  selected: Date | null;
  onChange: (date: Date | null) => void;
  dateFormat?: string;
  className?: string;
  placeholderText?: string;
}> = ({ selected, onChange, dateFormat, className, placeholderText }) => {
  return (
    <div style={{ position: 'relative', width: '100%', backgroundColor: '#fff', zIndex: 10 }}>
      <DatePicker
        selected={selected}
        onChange={onChange}
        dateFormat={dateFormat || "MMMM d, yyyy"}
        className={className || "w-full p-2 border border-gray-300 rounded"}
        placeholderText={placeholderText || "Select release date"}
        popperProps={{
          positionFixed: true
        }}
        popperModifiers={[
          {
            name: 'preventOverflow',
            options: {
              enabled: true,
              escapeWithReference: false,
              boundary: window
            },
          },
        ]}
        wrapperClassName="date-picker-wrapper"
      />
    </div>
  );
};

const ReleaseNotes: React.FC = () => {
  // State variables
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedNote, setSelectedNote] = useState<ReleaseNote | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [formData, setFormData] = useState<ReleaseNoteForm>({
    title: '',
    description: '',
    version: '',
    release_date: new Date(),
    is_visible: true,
  });

  // Load release notes data
  const fetchReleaseNotes = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<ReleaseNote[]>('/api/release-notes');
      setReleaseNotes(response.data);
    } catch (error) {
      console.error('Error fetching release notes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchReleaseNotes();
  }, []);

  // Handle form field changes
  const handleFormChange = (field: keyof ReleaseNoteForm, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    setFormData(prev => ({
      ...prev,
      release_date: date
    }));
  };

  // Open dialog for creating a new release note
  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setFormData({
      title: '',
      description: '',
      version: '',
      release_date: new Date(),
      is_visible: true,
    });
    setOpenDialog(true);
  };

  // Open dialog for editing an existing release note
  const handleOpenEditDialog = (note: ReleaseNote) => {
    setDialogMode('edit');
    setSelectedNote(note);
    setFormData({
      title: note.title,
      description: note.description,
      version: note.version || '',
      release_date: note.release_date ? new Date(note.release_date) : null,
      is_visible: note.is_visible,
    });
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedNote(null);
  };

  // Submit form (create or edit)
  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Ensure title and description are not empty
      if (!formData.title.trim()) {
        alert('Title is required');
        setLoading(false);
        return;
      }
      
      if (!formData.description.trim()) {
        alert('Description is required');
        setLoading(false);
        return;
      }
      
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        version: formData.version.trim() || null,
        release_date: formData.release_date ? formData.release_date.toISOString().split('T')[0] : null,
        is_visible: formData.is_visible
      };
      
      console.log('Submitting release note with payload:', payload);
      
      let success = false;
      if (dialogMode === 'create') {
        try {
          const response = await apiService.post('/api/release-notes', payload);
          console.log('Create response:', response);
          success = true;
        } catch (err: any) {
          console.error('Error in create API call:', err);
          console.error('Error response data:', err.response?.data);
          
          // Extract more detailed error information
          let errorMessage = 'Failed to create release note';
          if (err.response?.data) {
            if (typeof err.response.data === 'string') {
              errorMessage = err.response.data;
            } else if (err.response.data.detail) {
              if (Array.isArray(err.response.data.detail)) {
                errorMessage = err.response.data.detail.map((e: any) => e.msg || e).join(', ');
              } else {
                errorMessage = err.response.data.detail;
              }
            } else {
              errorMessage = JSON.stringify(err.response.data);
            }
          }
          
          alert(`Error: ${errorMessage}`);
        }
      } else if (dialogMode === 'edit' && selectedNote) {
        try {
          const response = await apiService.put(`/api/release-notes/${selectedNote.id}`, payload);
          console.log('Update response:', response);
          success = true;
        } catch (err: any) {
          console.error('Error in update API call:', err);
          console.error('Error response data:', err.response?.data);
          
          // Extract more detailed error information
          let errorMessage = 'Failed to update release note';
          if (err.response?.data) {
            if (typeof err.response.data === 'string') {
              errorMessage = err.response.data;
            } else if (err.response.data.detail) {
              if (Array.isArray(err.response.data.detail)) {
                errorMessage = err.response.data.detail.map((e: any) => e.msg || e).join(', ');
              } else {
                errorMessage = err.response.data.detail;
              }
            } else {
              errorMessage = JSON.stringify(err.response.data);
            }
          }
          
          alert(`Error: ${errorMessage}`);
        }
      }
      
      // Only close dialog and refresh data if the API call was successful
      if (success) {
        await fetchReleaseNotes();
        handleCloseDialog();
      }
    } catch (error) {
      console.error('Error saving release note:', error);
      alert('Failed to save release note. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle visibility of a release note
  const handleToggleVisibility = async (id: number, currentState: boolean) => {
    try {
      setLoading(true);
      await apiService.put(`/api/release-notes/${id}`, {
        is_visible: !currentState
      });
      await fetchReleaseNotes();
    } catch (error) {
      console.error('Error toggling visibility:', error);
    } finally {
      setLoading(false);
    }
  };

  // Delete a single release note
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this release note?')) {
      return;
    }
    
    try {
      setLoading(true);
      await apiService.delete(`/api/release-notes/${id}`);
      await fetchReleaseNotes();
    } catch (error) {
      console.error('Error deleting release note:', error);
    } finally {
      setLoading(false);
    }
  };

  // Select or deselect a row
  const handleSelectRow = (id: number) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(itemId => itemId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Select or deselect all rows
  const handleSelectAll = () => {
    if (selectedIds.length === releaseNotes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(releaseNotes.map(note => note.id));
    }
  };

  // Delete selected release notes
  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} release notes?`)) {
      return;
    }
    
    try {
      setLoading(true);
      await apiService.delete('/api/release-notes', {
        data: { ids: selectedIds }
      });
      setSelectedIds([]);
      await fetchReleaseNotes();
    } catch (error) {
      console.error('Error deleting selected release notes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  return (
    <PageContainer>
      <Typography 
        variant="subtitle1" 
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Release Notes
      </Typography>

      <Paper elevation={2} className="p-6">
        <Box className="flex justify-between items-center mb-4">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Add Release Note
          </Button>
        </Box>

        {loading && (
          <LoadingState message="Loading release notes..." containerClassName="min-h-[400px]" />
        )}

        {selectedIds.length > 0 && (
          <Box className="mb-4 flex justify-end">
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteSelected}
            >
              Delete Selected ({selectedIds.length})
            </Button>
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < releaseNotes.length}
                    checked={releaseNotes.length > 0 && selectedIds.length === releaseNotes.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Release Date</TableCell>
                <TableCell>Visibility</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {releaseNotes.length > 0 ? (
                releaseNotes.map(note => (
                  <TableRow key={note.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(note.id)}
                        onChange={() => handleSelectRow(note.id)}
                      />
                    </TableCell>
                    <TableCell>{note.title}</TableCell>
                    <TableCell>{note.version || '-'}</TableCell>
                    <TableCell>{formatDate(note.release_date)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={note.is_visible ? 'Visible' : 'Hidden'} 
                        color={note.is_visible ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title={note.is_visible ? 'Currently visible to users - Click to hide' : 'Currently hidden from users - Click to make visible'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleVisibility(note.id, note.is_visible)}
                          >
                            {note.is_visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(note)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(note.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" className="py-4">
                      No release notes found. Click the "Add Release Note" button to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add New Release Note' : 'Edit Release Note'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 1 }}>
          <Box className="flex flex-col">
            <TextField
              label="Title"
              fullWidth
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              required
              margin="dense"
              size="small"
            />
            <TextField
              label="Version"
              fullWidth
              value={formData.version}
              onChange={(e) => handleFormChange('version', e.target.value)}
              placeholder="1.0.0"
              margin="dense"
              size="small"
            />
            <Box sx={{ mt: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Release Date</Typography>
              <CustomDatePicker
                selected={formData.release_date}
                onChange={handleDateChange}
                dateFormat="MMMM d, yyyy"
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                placeholderText="Select release date"
              />
            </Box>
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={6}
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              required
              placeholder="Describe the features, fixes, or changes in this release."
              margin="dense"
              InputProps={{
                style: { minHeight: '60px' }
              }}
              sx={{
                mt: 1,
                '& .MuiOutlinedInput-root': {
                  height: 'auto',
                },
                '& .MuiInputBase-inputMultiline': {
                  minHeight: '60px !important',
                }
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_visible}
                  onChange={(e) => handleFormChange('is_visible', e.target.checked)}
                />
              }
              label="Visible to users"
              sx={{ mt: 0.5, mb: 0.5 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.title || !formData.description}
          >
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default ReleaseNotes; 